import { useEffect, useState } from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { size } from "lodash";
import { Card } from "react-bootstrap";
import { capitalCase } from "change-case";

import IconSpinner from "components/IconSpinner";
import { getSecurityDetailsRequest } from "services/adminService";
import { getEmployerContributionAbility } from "services/employerService";
import IconTable, { type IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import { kvPairs } from "utils/typeUtils";

interface OwnProps {
  // PropTypes had companyId as PropTypes.string (not .isRequired) → optional
  companyId?: string;
}

type Props = WithApolloClient<OwnProps>;

// The `counts` object shared by both service responses (a flat map of numbers);
// `kvPairs` only needs a plain object, so a loose shape suffices.
type SecurityCounts = Record<string, number>;

// KvPair is not exported from typeUtils; derive the row shape from kvPairs.
type KvPairRow = ReturnType<typeof kvPairs>[number];

const EntityPayrollDetails = ({ client, companyId }: Props) => {
  const [counts, setCounts] = useState<KvPairRow[]>([]);
  const [isInitialFetch, setIsInitialFetch] = useState(true);

  useEffect(() => {
    const getCounts = async () => {
      let counts: SecurityCounts;
      if (companyId) {
        const response = await getEmployerContributionAbility(
          // client is optional in WithApolloClient; service requires it
          client as ApolloClient<object>,
          companyId
        );
        // getEmployerContributionAbility resolves to ContributionAbility | object;
        // the runtime payload always carries securityLimits.counts here.
        counts = (response as { securityLimits: { counts: SecurityCounts } })
          .securityLimits.counts;
      } else {
        const response = await getSecurityDetailsRequest(
          // client is optional in WithApolloClient; service requires it
          client as ApolloClient<object>
        );
        counts = response.counts;
      }
      setCounts(
        kvPairs(counts, {
          transformKey: capitalCase,
          transformValue: (v) => v,
        })
      );
    };
    if (isInitialFetch) {
      void getCounts().finally(() => {
        setIsInitialFetch(false);
      });
    }
  }, [isInitialFetch]);

  const tableMetadata: IconTableColumn<KvPairRow>[] = [
    {
      key: "key",
      label: "Key",
      format: (value) => {
        return capitalCase(value as string);
      },
    },
    {
      key: "value",
      label: "Value",
    },
  ];

  if (isInitialFetch) {
    return <IconSpinner centered />;
  }

  return (
    <>
      <h1 className="page-title">Payroll Details</h1>
      <Card>
        <IconTableHeader tableCount={size(counts)} tableHeader="Counts" />
        <IconTable columns={tableMetadata} data={counts} />
      </Card>
    </>
  );
};

export default withApollo<Props>(EntityPayrollDetails);
